import axios from "axios";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalAccessToken,
  setLocalRefreshToken,
} from "@/lib/storeManager";

/** Access token TTL is 15 minutes — refresh slightly earlier. */
export const ACCESS_TOKEN_REFRESH_INTERVAL_MS = 14 * 60 * 1000;

type RefreshResponse = {
  access?: string;
  refresh?: string;
  data?: {
    access?: string;
    refresh?: string;
  };
};

let refreshPromise: Promise<string | null> | null = null;
let intervalId: ReturnType<typeof setInterval> | null = null;

function extractTokens(payload: RefreshResponse) {
  const access = payload.access ?? payload.data?.access;
  const refresh = payload.refresh ?? payload.data?.refresh;
  return { access, refresh };
}

/**
 * POST /auth/token-refresh/ with JSON body { refresh }.
 * Uses plain axios (not axiosConfig) so 401 retry logic does not recurse.
 */
export async function refreshAccessToken(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) {
    return null;
  }

  // Coalesce concurrent refresh calls (interceptor + timer).
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const { data } = await axios.post<RefreshResponse>(
        `${import.meta.env.VITE_API_BASE_URL}/auth/token-refresh/`,
        { refresh },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Tenant-Id": getTenantId() ?? "",
          },
        }
      );

      const tokens = extractTokens(data ?? {});
      if (!tokens.access) {
        throw new Error("Token refresh response missing access token.");
      }

      setLocalAccessToken(tokens.access);
      if (tokens.refresh) {
        setLocalRefreshToken(tokens.refresh);
      }
      // Request interceptor reads getAccessToken() on each call.

      return tokens.access;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function refreshQuietly() {
  if (!getAccessToken() || !getRefreshToken()) {
    return;
  }
  try {
    await refreshAccessToken();
  } catch {
    // Keep session; next API 401 will retry or force logout.
  }
}

/** Start proactive refresh every 14 minutes (access TTL = 15 min). */
export function startAccessTokenRefreshScheduler() {
  if (!getRefreshToken()) {
    return;
  }

  // Idempotent — do not reset the timer on route changes / remounts.
  if (intervalId !== null) {
    return;
  }

  intervalId = setInterval(() => {
    void refreshQuietly();
  }, ACCESS_TOKEN_REFRESH_INTERVAL_MS);

  // Also refresh when the tab becomes visible again after being idle.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }
}

function onVisibilityChange() {
  if (document.visibilityState === "visible") {
    void refreshQuietly();
  }
}

export function stopAccessTokenRefreshScheduler() {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }
}

/** Clear tokens and stop the refresh loop (used when refresh fails hard). */
export function forceLogoutAfterRefreshFailure() {
  stopAccessTokenRefreshScheduler();
  clearLocalStorage();
}
